import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import Select from "react-select";

import axios from "../axiosConfig";

import {
  customSelectStyles,
} from "../styles/reactSelectStyles";

import {
  FaArrowLeft,
  FaCreditCard,
  FaIndustry,
  FaMapMarkerAlt,
  FaPlus,
  FaSave,
  FaTrashAlt,
} from "react-icons/fa";

import {
  useTranslation,
} from "react-i18next";

import {
  readIndustryCache,
  writeIndustryCache,
} from "../lib/industryEditCache";

import "../styles/EditIndustry.css";

/* =========================================================
   Options
   ========================================================= */

const INDUSTRY_OPTIONS = [
  "Agriculture",
  "Automotive",
  "Construction",
  "Education",
  "Energy",
  "Financial Services",
  "Food & Beverage",
  "Healthcare",
  "Hospitality",
  "Logistics",
  "Manufacturing",
  "Media & Entertainment",
  "Mining",
  "Pharmaceuticals",
  "Real Estate",
  "Retail",
  "Technology",
  "Utilities",
  "Other",
];

const COUNTRY_OPTIONS = [
  { value: "Austria", key: "austria", code: "at" },
  { value: "Belgium", key: "belgium", code: "be" },
  { value: "Croatia", key: "croatia", code: "hr" },
  { value: "Czech Republic", key: "czech_republic", code: "cz" },
  { value: "Denmark", key: "denmark", code: "dk" },
  { value: "Estonia", key: "estonia", code: "ee" },
  { value: "Finland", key: "finland", code: "fi" },
  { value: "France", key: "france", code: "fr" },
  { value: "Germany", key: "germany", code: "de" },
  { value: "Hungary", key: "hungary", code: "hu" },
  { value: "Italy", key: "italy", code: "it" },
  { value: "Netherlands", key: "netherlands", code: "nl" },
  { value: "Norway", key: "norway", code: "no" },
  { value: "Poland", key: "poland", code: "pl" },
  { value: "Portugal", key: "portugal", code: "pt" },
  { value: "Spain", key: "spain", code: "es" },
  { value: "Sweden", key: "sweden", code: "se" },
  { value: "Switzerland", key: "switzerland", code: "ch" },
  { value: "United Kingdom", key: "united_kingdom", code: "gb" },
];

const PHONE_NUMBER_OPTIONS = [
  { value: "+43", key: "austria", code: "at" },
  { value: "+32", key: "belgium", code: "be" },
  { value: "+359", key: "bulgaria", code: "bg" },
  { value: "+385", key: "croatia", code: "hr" },
  { value: "+357", key: "cyprus", code: "cy" },
  { value: "+420", key: "czech_republic", code: "cz" },
  { value: "+45", key: "denmark", code: "dk" },
  { value: "+372", key: "estonia", code: "ee" },
  { value: "+358", key: "finland", code: "fi" },
  { value: "+33", key: "france", code: "fr" },
  { value: "+49", key: "germany", code: "de" },
  { value: "+30", key: "greece", code: "gr" },
  { value: "+36", key: "hungary", code: "hu" },
  { value: "+354", key: "iceland", code: "is" },
  { value: "+353", key: "ireland", code: "ie" },
  { value: "+39", key: "italy", code: "it" },
  { value: "+371", key: "latvia", code: "lv" },
  { value: "+370", key: "lithuania", code: "lt" },
  { value: "+352", key: "luxembourg", code: "lu" },
  { value: "+356", key: "malta", code: "mt" },
  { value: "+31", key: "netherlands", code: "nl" },
  { value: "+47", key: "norway", code: "no" },
  { value: "+48", key: "poland", code: "pl" },
  { value: "+351", key: "portugal", code: "pt" },
  { value: "+40", key: "romania", code: "ro" },
  { value: "+421", key: "slovakia", code: "sk" },
  { value: "+386", key: "slovenia", code: "si" },
  { value: "+34", key: "spain", code: "es" },
  { value: "+46", key: "sweden", code: "se" },
  { value: "+41", key: "switzerland", code: "ch" },
  { value: "+44", key: "united_kingdom", code: "gb" },
];

const CURRENCY_OPTIONS = [
  { value: "ALL", key: "all", code: "al" },
  { value: "BAM", key: "bam", code: "ba" },
  { value: "BGN", key: "bgn", code: "bg" },
  { value: "BYN", key: "byn", code: "by" },
  { value: "CHF", key: "chf", code: "ch" },
  { value: "CZK", key: "czk", code: "cz" },
  { value: "DKK", key: "dkk", code: "dk" },
  { value: "EUR", key: "eur", code: "eu" },
  { value: "GBP", key: "gbp", code: "gb" },
  { value: "HRK", key: "hrk", code: "hr" },
  { value: "HUF", key: "huf", code: "hu" },
  { value: "ISK", key: "isk", code: "is" },
  { value: "MKD", key: "mkd", code: "mk" },
  { value: "NOK", key: "nok", code: "no" },
  { value: "PLN", key: "pln", code: "pl" },
  { value: "RON", key: "ron", code: "ro" },
  { value: "RSD", key: "rsd", code: "rs" },
  { value: "RUB", key: "rub", code: "ru" },
  { value: "SEK", key: "sek", code: "se" },
  { value: "UAH", key: "uah", code: "ua" },
];

const PAYMENT_DUE_DATES = [
  { value: "same day", key: "same_day" },
  { value: "1 day", key: "d1" },
  { value: "3 days", key: "d3" },
  { value: "5 days", key: "d5" },
  { value: "10 days", key: "d10" },
  { value: "15 days", key: "d15" },
  { value: "30 days", key: "d30" },
  { value: "45 days", key: "d45" },
  { value: "60 days", key: "d60" },
  { value: "90 days", key: "d90" },
  { value: "120 days", key: "d120" },
];

const DEFAULT_INDUSTRY = {
  companyName: "",
  industryType: "",
  otherIndustry: "",

  goodsProduced: "",
  transportationNeeds: "",
  productionCapacity: "",
  currentTransportProviders: "",
  avgShippingVolumePerMonth: "",
  frequentDestinations: "",
  specialRequirements: "",

  address: "",
  city: "",
  postalCode: "",
  country: "",

  mainContactName: "",
  mainContactTitle: "",
  contactEmail: "",
  invoiceEmail: "",
  phoneNumberCountryCode: "",
  phoneNumber: "",
  contacts: [],
  website: "",

  bankName: "",
  accountHolderName: "",
  accountNumber: "",
  IBAN: "",
  SWIFT_BIC: "",
  currency: "",

  paymentMethodsAccepted: [],
  paymentDueDate: "",
  discountTerms: "",
  latePaymentPenalties: "",

  iva: "",
  VAT_TaxInformation: "",
  invoiceDeliveryMethod: "",

  additionalNotes: "",
};

/* =========================================================
   Helpers
   ========================================================= */

function normalizePMA(
  value
) {
  if (
    Array.isArray(
      value
    )
  ) {
    return value;
  }

  if (!value) {
    return [];
  }

  if (
    typeof value ===
    "string"
  ) {
    return value
      .split(",")
      .map(
        (
          item
        ) =>
          String(
            item ||
              ""
          ).trim()
      )
      .filter(
        Boolean
      );
  }

  return [];
}

function normalizeIvaValue(
  value
) {
  if (
    value ===
      undefined ||
    value ===
      null ||
    value ===
      ""
  ) {
    return "";
  }

  const normalized =
    String(
      value
    )
      .replace(
        ",",
        "."
      )
      .trim();

  const parsed =
    Number(
      normalized
    );

  if (
    !Number.isFinite(
      parsed
    )
  ) {
    return "";
  }

  return parsed.toFixed(
    2
  );
}

function normalizeContacts(
  value
) {
  if (
    Array.isArray(
      value
    )
  ) {
    return value.map(
      (
        contact
      ) => ({
        contactName:
          contact?.contactName ||
          "",

        contactTitle:
          contact?.contactTitle ||
          "",

        contactEmail:
          contact?.contactEmail ||
          "",

        phoneNumberCountryCode:
          contact?.phoneNumberCountryCode ||
          "",

        phoneNumber:
          contact?.phoneNumber ||
          "",
      })
    );
  }

  if (
    typeof value ===
      "string" &&
    value.trim()
  ) {
    try {
      const parsed =
        JSON.parse(
          value
        );

      return normalizeContacts(
        parsed
      );
    } catch {
      return [];
    }
  }

  return [];
}

const normalizeIndustry =
  (
    value
  ) => {
    const base = {
      ...DEFAULT_INDUSTRY,

      ...(value ||
        {}),
    };

    return {
      ...base,

      iva:
        normalizeIvaValue(
          base.iva
        ),

      paymentMethodsAccepted:
        normalizePMA(
          base.paymentMethodsAccepted
        ),

      contacts:
        normalizeContacts(
          base.contacts
        ),
    };
  };

const countOnlyAlphaNumeric =
  (
    value = ""
  ) =>
    String(
      value
    ).replace(
      /[^a-zA-Z0-9]/g,
      ""
    ).length;

/* =========================================================
   EditIndustry
   ========================================================= */

export default function EditIndustry() {
  const {
    t,
  } = useTranslation(
    "edit_industry"
  );

  const {
    industryId,
  } = useParams();

  const navigate =
    useNavigate();

  const location =
    useLocation();

  const prefill =
    location.state
      ?.prefill ??
    null;

  const [
    cacheInit,
  ] = useState(
    () =>
      readIndustryCache(
        industryId
      )
  );

  const [
    bootstrapped,
    setBootstrapped,
  ] = useState(
    () =>
      Boolean(
        prefill ||
          cacheInit
      )
  );

  const [
    industry,
    setIndustry,
  ] = useState(
    () =>
      normalizeIndustry({
        ...(cacheInit ||
          {}),

        ...(prefill ||
          {}),
      })
  );

  const [
    invalidFields,
    setInvalidFields,
  ] = useState({});

  const [
    errorMessage,
    setErrorMessage,
  ] = useState("");

  const [
    showSuccessPopup,
    setShowSuccessPopup,
  ] = useState(false);

  const [
    updatedIndustryName,
    setUpdatedIndustryName,
  ] = useState("");

  const [
    isSaving,
    setIsSaving,
  ] = useState(false);

  const [
    activeSection,
    setActiveSection,
  ] = useState(
    "basic"
  );

  const dirtyRef =
    useRef(false);

  const cacheTimerRef =
    useRef(null);

  const sectionRefs =
    useRef({});

  /* =======================================================
     Options
     ======================================================= */

  const industryTypeOptions =
    useMemo(
      () =>
        INDUSTRY_OPTIONS.map(
          (
            value
          ) => ({
            value,

            label:
              t(
                `options.industry.${value}`
              ),
          })
        ),
      [t]
    );

  const countryOptions =
    useMemo(
      () =>
        COUNTRY_OPTIONS.map(
          (
            option
          ) => ({
            ...option,

            label:
              t(
                `countries.${option.key}`
              ),
          })
        ),
      [t]
    );

  const phoneNumberOptions =
    useMemo(
      () =>
        PHONE_NUMBER_OPTIONS.map(
          (
            option
          ) => ({
            ...option,

            label:
              `${t(
                `countries.${option.key}`
              )} (${option.value})`,
          })
        ),
      [t]
    );

  const currencyOptions =
    useMemo(
      () =>
        CURRENCY_OPTIONS.map(
          (
            option
          ) => ({
            ...option,

            label:
              t(
                `currencies.${option.key}`
              ),
          })
        ),
      [t]
    );

  const paymentOptions =
    useMemo(
      () => [
        {
          value:
            "Bank Transfer",

          label:
            t(
              "payments.bank_transfer"
            ),
        },
        {
          value:
            "Credit/Debit Card",

          label:
            t(
              "payments.card"
            ),
        },
        {
          value:
            "Cash",

          label:
            t(
              "payments.cash"
            ),
        },
        {
          value:
            "MB WAY",

          label:
            t(
              "payments.mbway"
            ),
        },
        {
          value:
            "PayPal",

          label:
            t(
              "payments.paypal"
            ),
        },
      ],
      [t]
    );

  const paymentDueDateOptions =
    useMemo(
      () =>
        PAYMENT_DUE_DATES.map(
          (
            option
          ) => ({
            value:
              option.value,

            label:
              t(
                `dueOptions.${option.key}`
              ),
          })
        ),
      [t]
    );

  const invoiceDeliveryOptions =
    useMemo(
      () => [
        {
          value:
            "email",

          label:
            t(
              "invoice_delivery.email"
            ),
        },
        {
          value:
            "postal",

          label:
            t(
              "invoice_delivery.postal"
            ),
        },
        {
          value:
            "download",

          label:
            t(
              "invoice_delivery.download"
            ),
        },
      ],
      [t]
    );

  const paymentMultiStyles =
    useMemo(
      () => {
        const base =
          customSelectStyles ||
          {};

        return {
          ...base,

          control: (
            provided,
            state
          ) => ({
            ...(base.control
              ? base.control(
                  provided,
                  state
                )
              : provided),

            minHeight:
              40,

            height:
              40,

            overflow:
              "hidden",
          }),

          valueContainer: (
            provided,
            state
          ) => ({
            ...(base.valueContainer
              ? base.valueContainer(
                  provided,
                  state
                )
              : provided),

            flexWrap:
              "nowrap",

            overflowX:
              "auto",

            overflowY:
              "hidden",

            whiteSpace:
              "nowrap",

            alignItems:
              "center",

            paddingRight:
              68,

            gap: 5,
          }),

          multiValue: (
            provided,
            state
          ) => ({
            ...(base.multiValue
              ? base.multiValue(
                  provided,
                  state
                )
              : provided),

            flex:
              "0 0 auto",

            maxWidth:
              "none",

            margin: 0,
          }),

          multiValueLabel: (
            provided,
            state
          ) => ({
            ...(base.multiValueLabel
              ? base.multiValueLabel(
                  provided,
                  state
                )
              : provided),

            maxWidth:
              140,

            overflow:
              "hidden",

            textOverflow:
              "ellipsis",

            whiteSpace:
              "nowrap",
          }),
        };
      },
      []
    );

  /* =======================================================
     Cache
     ======================================================= */

  useEffect(() => {
    return () => {
      if (
        cacheTimerRef.current
      ) {
        clearTimeout(
          cacheTimerRef.current
        );
      }
    };
  }, []);

  const scheduleCacheWrite =
    (
      nextIndustry
    ) => {
      try {
        if (
          cacheTimerRef.current
        ) {
          clearTimeout(
            cacheTimerRef.current
          );
        }

        cacheTimerRef.current =
          setTimeout(
            () => {
              writeIndustryCache(
                industryId,
                nextIndustry
              );
            },
            350
          );
      } catch {
        // ignore cache errors
      }
    };

  /* =======================================================
     Fetch
     ======================================================= */

  useEffect(() => {
    const controller =
      new AbortController();

    (async () => {
      try {
        const {
          data,
        } =
          await axios.get(
            `/industries/${industryId}`,
            {
              signal:
                controller.signal,
            }
          );

        const hydrated =
          normalizeIndustry(
            data
          );

        writeIndustryCache(
          industryId,
          hydrated
        );

        setBootstrapped(
          true
        );

        if (
          dirtyRef.current
        ) {
          return;
        }

        setIndustry(
          (
            previous
          ) => {
            try {
              return JSON.stringify(
                previous
              ) ===
                JSON.stringify(
                  hydrated
                )
                ? previous
                : hydrated;
            } catch {
              return hydrated;
            }
          }
        );
      } catch (error) {
        if (
          String(
            error?.name
          ) ===
            "CanceledError" ||
          String(
            error?.name
          ) ===
            "AbortError"
        ) {
          return;
        }

        console.error(
          "Error fetching industry:",
          error
        );

        setErrorMessage(
          t(
            "messages.fetch_error"
          )
        );
      }
    })();

    return () =>
      controller.abort();
  }, [
    industryId,
    t,
  ]);

  useEffect(() => {
    if (!prefill) {
      return;
    }

    const normalized =
      normalizeIndustry(
        prefill
      );

    writeIndustryCache(
      industryId,
      normalized
    );

    setBootstrapped(
      true
    );
  }, [
    industryId,
    prefill,
  ]);

  /* =======================================================
     Scroll spy
     ======================================================= */

  useEffect(() => {
    if (
      !bootstrapped
    ) {
      return undefined;
    }

    const sectionKeys = [
      "basic",
      "contacts",
      "billing",
    ];

    let animationFrameId =
      null;

    const updateActiveSection =
      () => {
        animationFrameId =
          null;

        const activationLine =
          190;

        let nextActive =
          "basic";

        for (
          let index = 0;
          index <
          sectionKeys.length;
          index += 1
        ) {
          const key =
            sectionKeys[index];

          const section =
            sectionRefs.current[
              key
            ];

          if (!section) {
            continue;
          }

          const rect =
            section.getBoundingClientRect();

          if (
            rect.top <=
            activationLine
          ) {
            nextActive =
              key;
          } else {
            break;
          }
        }

        setActiveSection(
          (
            current
          ) =>
            current ===
            nextActive
              ? current
              : nextActive
        );
      };

    const scheduleUpdate =
      () => {
        if (
          animationFrameId !==
          null
        ) {
          return;
        }

        animationFrameId =
          window.requestAnimationFrame(
            updateActiveSection
          );
      };

    document.addEventListener(
      "scroll",
      scheduleUpdate,
      true
    );

    window.addEventListener(
      "resize",
      scheduleUpdate
    );

    updateActiveSection();

    return () => {
      document.removeEventListener(
        "scroll",
        scheduleUpdate,
        true
      );

      window.removeEventListener(
        "resize",
        scheduleUpdate
      );

      if (
        animationFrameId !==
        null
      ) {
        window.cancelAnimationFrame(
          animationFrameId
        );
      }
    };
  }, [
    bootstrapped,
  ]);

  const scrollToSection =
    (
      sectionKey
    ) => {
      setActiveSection(
        sectionKey
      );

      sectionRefs.current[
        sectionKey
      ]?.scrollIntoView({
        behavior:
          "smooth",

        block:
          "start",
      });
    };

  /* =======================================================
     Errors / fields
     ======================================================= */

  const clearFieldError =
    (
      fieldName
    ) => {
      setInvalidFields(
        (
          previous
        ) => {
          if (
            !previous[
              fieldName
            ]
          ) {
            return previous;
          }

          const updated = {
            ...previous,
          };

          delete updated[
            fieldName
          ];

          return updated;
        }
      );
    };

  const setFieldValue =
    (
      fieldName,
      value
    ) => {
      dirtyRef.current =
        true;

      setIndustry(
        (
          previous
        ) => {
          const next = {
            ...previous,

            [fieldName]:
              value,
          };

          scheduleCacheWrite(
            next
          );

          return next;
        }
      );

      clearFieldError(
        fieldName
      );
    };

  const handleInputChange =
    (
      event
    ) => {
      const {
        name,
        value,
      } = event.target;

      dirtyRef.current =
        true;

      if (
        name ===
        "iva"
      ) {
        const cleaned =
          value
            .replace(
              ",",
              "."
            )
            .replace(
              /[^0-9.]/g,
              ""
            );

        setIndustry(
          (
            previous
          ) => {
            const next = {
              ...previous,

              iva:
                cleaned,
            };

            scheduleCacheWrite(
              next
            );

            return next;
          }
        );

        setInvalidFields(
          (
            previous
          ) => {
            const updated = {
              ...previous,
            };

            if (
              !String(
                cleaned
              ).trim()
            ) {
              delete updated.iva;

              return updated;
            }

            const parsed =
              Number(
                cleaned
              );

            if (
              !Number.isFinite(
                parsed
              ) ||
              parsed < 0 ||
              parsed > 100
            ) {
              updated.iva =
                t(
                  "errors.iva_invalid"
                );
            } else {
              delete updated.iva;
            }

            return updated;
          }
        );

        return;
      }

      setIndustry(
        (
          previous
        ) => {
          const next = {
            ...previous,

            [name]:
              value,
          };

          scheduleCacheWrite(
            next
          );

          return next;
        }
      );

      if (
        name ===
        "IBAN"
      ) {
        setInvalidFields(
          (
            previous
          ) => {
            const updated = {
              ...previous,
            };

            if (
              !String(
                value
              ).trim()
            ) {
              delete updated.IBAN;
            } else if (
              countOnlyAlphaNumeric(
                value
              ) < 15
            ) {
              updated.IBAN =
                t(
                  "errors.IBAN_min"
                );
            } else {
              delete updated.IBAN;
            }

            return updated;
          }
        );

        return;
      }

      if (
        name ===
        "SWIFT_BIC"
      ) {
        setInvalidFields(
          (
            previous
          ) => {
            const updated = {
              ...previous,
            };

            if (
              !String(
                value
              ).trim()
            ) {
              delete updated.SWIFT_BIC;
            } else if (
              countOnlyAlphaNumeric(
                value
              ) < 8
            ) {
              updated.SWIFT_BIC =
                t(
                  "errors.SWIFT_BIC_min"
                );
            } else {
              delete updated.SWIFT_BIC;
            }

            return updated;
          }
        );

        return;
      }

      clearFieldError(
        name
      );
    };

  /* =======================================================
     Contacts
     ======================================================= */

  const handleAddContact =
    () => {
      dirtyRef.current =
        true;

      setIndustry(
        (
          previous
        ) => {
          const next = {
            ...previous,

            contacts: [
              ...(previous.contacts ||
                []),

              {
                contactName:
                  "",

                contactTitle:
                  "",

                contactEmail:
                  "",

                phoneNumberCountryCode:
                  "",

                phoneNumber:
                  "",
              },
            ],
          };

          scheduleCacheWrite(
            next
          );

          return next;
        }
      );
    };

  const handleRemoveContact =
    (
      index
    ) => {
      dirtyRef.current =
        true;

      setIndustry(
        (
          previous
        ) => {
          const next = {
            ...previous,

            contacts: (
              previous.contacts ||
              []
            ).filter(
              (
                _,
                contactIndex
              ) =>
                contactIndex !==
                index
            ),
          };

          scheduleCacheWrite(
            next
          );

          return next;
        }
      );

      setInvalidFields(
        (
          previous
        ) => {
          const updated = {
            ...previous,
          };

          Object.keys(
            updated
          ).forEach(
            (
              key
            ) => {
              if (
                key.startsWith(
                  `contacts-${index}-`
                )
              ) {
                delete updated[
                  key
                ];
              }
            }
          );

          return updated;
        }
      );
    };

  const handleContactChange =
    (
      index,
      field,
      value
    ) => {
      dirtyRef.current =
        true;

      setIndustry(
        (
          previous
        ) => {
          const next = {
            ...previous,

            contacts: (
              previous.contacts ||
              []
            ).map(
              (
                contact,
                contactIndex
              ) =>
                contactIndex ===
                index
                  ? {
                      ...contact,

                      [field]:
                        value,
                    }
                  : contact
            ),
          };

          scheduleCacheWrite(
            next
          );

          return next;
        }
      );

      setInvalidFields(
        (
          previous
        ) => {
          const updated = {
            ...previous,
          };

          delete updated[
            `contacts-${index}-${field}`
          ];

          if (
            field ===
            "contactEmail"
          ) {
            Object.keys(
              updated
            ).forEach(
              (
                key
              ) => {
                if (
                  key.startsWith(
                    "contacts-"
                  ) &&
                  key.endsWith(
                    "-contactEmail"
                  )
                ) {
                  delete updated[
                    key
                  ];
                }
              }
            );
          }

          return updated;
        }
      );
    };

  /* =======================================================
     Validation
     ======================================================= */

  const validateAll =
    () => {
      const errors = {};

      if (
        !String(
          industry.companyName ||
            ""
        ).trim()
      ) {
        errors.companyName =
          t(
            "errors.companyName"
          );
      }

      if (
        String(
          industry.IBAN ||
            ""
        ).trim() &&
        countOnlyAlphaNumeric(
          industry.IBAN
        ) < 15
      ) {
        errors.IBAN =
          t(
            "errors.IBAN_min"
          );
      }

      if (
        String(
          industry.SWIFT_BIC ||
            ""
        ).trim() &&
        countOnlyAlphaNumeric(
          industry.SWIFT_BIC
        ) < 8
      ) {
        errors.SWIFT_BIC =
          t(
            "errors.SWIFT_BIC_min"
          );
      }

      if (
        String(
          industry.iva ||
            ""
        ).trim()
      ) {
        const parsedIva =
          Number(
            String(
              industry.iva
            ).replace(
              ",",
              "."
            )
          );

        if (
          !Number.isFinite(
            parsedIva
          ) ||
          parsedIva < 0 ||
          parsedIva > 100
        ) {
          errors.iva =
            t(
              "errors.iva_invalid"
            );
        }
      }

      const normalizedEmailMap =
        {};

      (
        industry.contacts ||
        []
      ).forEach(
        (
          contact,
          index
        ) => {
          const email =
            String(
              contact.contactEmail ||
                ""
            )
              .trim()
              .toLowerCase();

          if (!email) {
            return;
          }

          if (
            !normalizedEmailMap[
              email
            ]
          ) {
            normalizedEmailMap[
              email
            ] = [];
          }

          normalizedEmailMap[
            email
          ].push(index);
        }
      );

      Object.values(
        normalizedEmailMap
      ).forEach(
        (
          indexes
        ) => {
          if (
            indexes.length >
            1
          ) {
            indexes.forEach(
              (
                index
              ) => {
                errors[
                  `contacts-${index}-contactEmail`
                ] =
                  t(
                    "errors.duplicate_contact_email"
                  );
              }
            );
          }
        }
      );

      return errors;
    };

  const getSectionFromErrors =
    (
      errors
    ) => {
      const keys =
        Object.keys(
          errors
        );

      if (
        keys.length ===
        0
      ) {
        return null;
      }

      if (
        keys.some(
          (
            key
          ) =>
            [
              "companyName",
              "industryType",
              "otherIndustry",
            ].includes(
              key
            )
        )
      ) {
        return "basic";
      }

      if (
        keys.some(
          (
            key
          ) =>
            key.startsWith(
              "contacts-"
            )
        )
      ) {
        return "contacts";
      }

      return "billing";
    };

  /* =======================================================
     Submit
     ======================================================= */

  const handleSubmit =
    async (
      event
    ) => {
      event.preventDefault();

      if (
        isSaving
      ) {
        return;
      }

      setErrorMessage(
        ""
      );

      const errors =
        validateAll();

      setInvalidFields(
        errors
      );

      if (
        Object.keys(
          errors
        ).length >
        0
      ) {
        setErrorMessage(
          t(
            "messages.fill_required"
          )
        );

        const section =
          getSectionFromErrors(
            errors
          );

        if (section) {
          setTimeout(
            () => {
              scrollToSection(
                section
              );
            },
            0
          );
        }

        return;
      }

      setIsSaving(
        true
      );

      try {
        const payload = {
          ...industry,

          iva:
            String(
              industry.iva ||
                ""
            ).trim()
              ? Number(
                  String(
                    industry.iva
                  ).replace(
                    ",",
                    "."
                  )
                ).toFixed(
                  2
                )
              : null,

          contacts:
            industry.contacts ||
            [],

          paymentMethodsAccepted:
            normalizePMA(
              industry.paymentMethodsAccepted
            ),
        };

        await axios.put(
          `/industries/${industryId}`,
          payload
        );

        writeIndustryCache(
          industryId,
          payload
        );

        setUpdatedIndustryName(
          payload.companyName ||
            ""
        );

        setShowSuccessPopup(
          true
        );

        setTimeout(() => {
          navigate(
            "/clients/industry?view=industries"
          );
        }, 1200);
      } catch (error) {
        console.error(
          "Error updating industry:",
          error
        );

        setIsSaving(
          false
        );

        setShowSuccessPopup(
          false
        );

        setErrorMessage(
          error.response
            ?.data
            ?.error ||
            t(
              "messages.update_error_fallback"
            )
        );
      }
    };

  /* =======================================================
     Navigation
     ======================================================= */

  const navigationItems =
    useMemo(
      () => [
        {
          key:
            "basic",

          icon:
            <FaIndustry />,

          title:
            t(
              "nav.basic"
            ),

          subtitle:
            t(
              "nav.basic_subtitle"
            ),
        },
        {
          key:
            "contacts",

          icon:
            <FaMapMarkerAlt />,

          title:
            t(
              "nav.contacts"
            ),

          subtitle:
            t(
              "nav.contacts_subtitle"
            ),
        },
        {
          key:
            "billing",

          icon:
            <FaCreditCard />,

          title:
            t(
              "nav.billing"
            ),

          subtitle:
            t(
              "nav.billing_subtitle"
            ),
        },
      ],
      [t]
    );

  /* =======================================================
     Loading
     ======================================================= */

  if (
    !bootstrapped
  ) {
    return (
      <div className="edit-industry-page">
        <div className="edit-industry-loading">
          <span className="edit-industry-loading__spinner" />

          <span>
            {t(
              "common.loading"
            )}
          </span>
        </div>
      </div>
    );
  }

  /* =======================================================
     Render
     ======================================================= */

  return (
    <div className="edit-industry-page">
      {showSuccessPopup && (
        <div className="edit-industry-successPopup-overlay">
          <div className="edit-industry-successPopup">
            <div className="edit-industry-successPopup-icon">
              ✓
            </div>

            <h3>
              {t(
                "success.updatedTitle"
              )}
            </h3>

            <p>
              {t(
                "success.updated",
                {
                  name:
                    updatedIndustryName,
                }
              )}
            </p>

            <button
              type="button"
              className="edit-industry-successPopup-btn"
              onClick={() =>
                navigate(
                  "/clients/industry?view=industries"
                )
              }
            >
              {t(
                "actions.ok"
              )}
            </button>
          </div>
        </div>
      )}

      <header className="edit-industry-header">
        <div className="edit-industry-header__left">
          <button
            type="button"
            className="edit-industry-back-button"
            onClick={() =>
              navigate(
                "/clients/industry?view=industries"
              )
            }
            aria-label={t(
              "actions.back"
            )}
          >
            <FaArrowLeft />
          </button>

          <div className="edit-industry-header__copy">
            <h1>
              {t(
                "header.title"
              )}
            </h1>

            <p>
              {industry.companyName ||
                t(
                  "header.company_fallback"
                )}
            </p>
          </div>
        </div>

        <div className="edit-industry-header__actions">
          <button
            type="button"
            className="edit-industry-header-cancel"
            onClick={() =>
              navigate(
                "/clients/industry?view=industries"
              )
            }
          >
            {t(
              "buttons.cancel"
            )}
          </button>

          <button
            type="submit"
            form="edit-industry-main-form"
            className="edit-industry-header-save"
            disabled={
              isSaving
            }
          >
            <FaSave />

            <span>
              {isSaving
                ? t(
                    "buttons.saving"
                  )
                : t(
                    "buttons.save_changes"
                  )}
            </span>
          </button>
        </div>
      </header>

      {errorMessage && (
        <div className="edit-industry-global-error">
          {
            errorMessage
          }
        </div>
      )}

      <div className="edit-industry-layout">
        <aside className="edit-industry-section-nav">
          <div className="edit-industry-section-nav__inner">
            {navigationItems.map(
              (
                item
              ) => (
                <button
                  type="button"
                  key={
                    item.key
                  }
                  className={`edit-industry-section-nav__item ${
                    activeSection ===
                    item.key
                      ? "is-active"
                      : ""
                  }`}
                  onClick={() =>
                    scrollToSection(
                      item.key
                    )
                  }
                >
                  <span className="edit-industry-section-nav__icon">
                    {
                      item.icon
                    }
                  </span>

                  <span className="edit-industry-section-nav__copy">
                    <strong>
                      {
                        item.title
                      }
                    </strong>

                    <small>
                      {
                        item.subtitle
                      }
                    </small>
                  </span>
                </button>
              )
            )}
          </div>
        </aside>

        <form
          id="edit-industry-main-form"
          className="edit-industry-form"
          onSubmit={
            handleSubmit
          }
          noValidate
        >
          {/* BASIC */}
          <section
            ref={(
              node
            ) => {
              sectionRefs.current.basic =
                node;
            }}
            data-section="basic"
            className="edit-industry-section"
          >
            <div className="edit-industry-section-header">
              <span className="edit-industry-section-header__icon">
                <FaIndustry />
              </span>

              <div>
                <h2>
                  {t(
                    "nav.basic"
                  )}
                </h2>

                <p>
                  {t(
                    "section_subtitles.basic"
                  )}
                </p>
              </div>
            </div>

            <div className="edit-industry-grid edit-industry-grid--2">
              <div className="edit-industry-field">
                <label htmlFor="companyName">
                  {t(
                    "labels.companyName"
                  )}
                  *
                </label>

                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={
                    industry.companyName
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.companyName"
                  )}
                  className={`edit-industry-input ${
                    invalidFields.companyName
                      ? "edit-industry-input-error"
                      : ""
                  }`}
                />

                {invalidFields.companyName && (
                  <p className="edit-industry-field-error">
                    {
                      invalidFields.companyName
                    }
                  </p>
                )}
              </div>

              <div className="edit-industry-field">
                <label>
                  {t(
                    "labels.industryType"
                  )}
                </label>

                <Select
                  options={
                    industryTypeOptions
                  }
                  value={
                    industryTypeOptions.find(
                      (
                        option
                      ) =>
                        option.value ===
                        industry.industryType
                    ) ||
                    null
                  }
                  onChange={(
                    option
                  ) => {
                    setFieldValue(
                      "industryType",
                      option?.value ||
                        ""
                    );

                    if (
                      option?.value !==
                      "Other"
                    ) {
                      setFieldValue(
                        "otherIndustry",
                        ""
                      );
                    }
                  }}
                  placeholder={t(
                    "placeholders.industryType"
                  )}
                  styles={
                    customSelectStyles
                  }
                  classNamePrefix="edit-industry-select"
                  isClearable
                />
              </div>
            </div>

            {industry.industryType ===
              "Other" && (
              <div className="edit-industry-grid edit-industry-grid--1">
                <div className="edit-industry-field">
                  <label htmlFor="otherIndustry">
                    {t(
                      "labels.otherIndustry"
                    )}
                  </label>

                  <input
                    type="text"
                    id="otherIndustry"
                    name="otherIndustry"
                    value={
                      industry.otherIndustry
                    }
                    onChange={
                      handleInputChange
                    }
                    placeholder={t(
                      "placeholders.otherIndustry"
                    )}
                    className="edit-industry-input"
                  />
                </div>
              </div>
            )}
          </section>

          {/* CONTACTS */}
          <section
            ref={(
              node
            ) => {
              sectionRefs.current.contacts =
                node;
            }}
            data-section="contacts"
            className="edit-industry-section"
          >
            <div className="edit-industry-section-header">
              <span className="edit-industry-section-header__icon">
                <FaMapMarkerAlt />
              </span>

              <div>
                <h2>
                  {t(
                    "nav.contacts"
                  )}
                </h2>

                <p>
                  {t(
                    "section_subtitles.contacts"
                  )}
                </p>
              </div>
            </div>

            <div className="edit-industry-grid edit-industry-grid--1">
              <div className="edit-industry-field">
                <label htmlFor="address">
                  {t(
                    "labels.address"
                  )}
                </label>

                <input
                  id="address"
                  name="address"
                  value={
                    industry.address
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.address"
                  )}
                  className="edit-industry-input"
                />
              </div>
            </div>

            <div className="edit-industry-grid edit-industry-grid--3">
              <div className="edit-industry-field">
                <label htmlFor="city">
                  {t(
                    "labels.city"
                  )}
                </label>

                <input
                  id="city"
                  name="city"
                  value={
                    industry.city
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.city"
                  )}
                  className="edit-industry-input"
                />
              </div>

              <div className="edit-industry-field">
                <label htmlFor="postalCode">
                  {t(
                    "labels.postalCode"
                  )}
                </label>

                <input
                  id="postalCode"
                  name="postalCode"
                  value={
                    industry.postalCode
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.postalCode"
                  )}
                  className="edit-industry-input"
                />
              </div>

              <div className="edit-industry-field">
                <label>
                  {t(
                    "labels.country"
                  )}
                </label>

                <Select
                  options={
                    countryOptions
                  }
                  value={
                    countryOptions.find(
                      (
                        option
                      ) =>
                        option.value ===
                        industry.country
                    ) ||
                    null
                  }
                  onChange={(
                    option
                  ) =>
                    setFieldValue(
                      "country",
                      option?.value ||
                        ""
                    )
                  }
                  formatOptionLabel={(
                    option
                  ) => (
                    <div className="edit-industry-option-with-icon">
                      <img
                        src={`https://flagcdn.com/w40/${option.code}.png`}
                        alt={
                          option.label
                        }
                        className="edit-industry-option-flag"
                      />

                      <span>
                        {
                          option.label
                        }
                      </span>
                    </div>
                  )}
                  placeholder={t(
                    "placeholders.country"
                  )}
                  styles={
                    customSelectStyles
                  }
                  classNamePrefix="edit-industry-select"
                  isClearable
                />
              </div>
            </div>

            <div className="edit-industry-grid edit-industry-grid--1">
              <div className="edit-industry-field">
                <label htmlFor="website">
                  {t(
                    "labels.website"
                  )}
                </label>

                <input
                  type="url"
                  id="website"
                  name="website"
                  value={
                    industry.website ||
                    ""
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.website"
                  )}
                  className="edit-industry-input"
                />
              </div>
            </div>

            <div className="edit-industry-grid edit-industry-grid--2">
              <div className="edit-industry-field">
                <label htmlFor="mainContactName">
                  {t(
                    "labels.mainContactName"
                  )}
                </label>

                <input
                  id="mainContactName"
                  name="mainContactName"
                  value={
                    industry.mainContactName
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.mainContactName"
                  )}
                  className="edit-industry-input"
                />
              </div>

              <div className="edit-industry-field">
                <label htmlFor="mainContactTitle">
                  {t(
                    "labels.mainContactTitle"
                  )}
                </label>

                <input
                  id="mainContactTitle"
                  name="mainContactTitle"
                  value={
                    industry.mainContactTitle
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.mainContactTitle"
                  )}
                  className="edit-industry-input"
                />
              </div>
            </div>

            <div className="edit-industry-grid edit-industry-grid--3">
              <div className="edit-industry-field">
                <label htmlFor="contactEmail">
                  {t(
                    "labels.contactEmail"
                  )}
                </label>

                <input
                  type="email"
                  id="contactEmail"
                  name="contactEmail"
                  value={
                    industry.contactEmail
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.contactEmail"
                  )}
                  className="edit-industry-input"
                />
              </div>

              <div className="edit-industry-field">
                <label>
                  {t(
                    "labels.phoneNumberCountryCode"
                  )}
                </label>

                <Select
                  options={
                    phoneNumberOptions
                  }
                  value={
                    phoneNumberOptions.find(
                      (
                        option
                      ) =>
                        option.value ===
                        industry.phoneNumberCountryCode
                    ) ||
                    null
                  }
                  onChange={(
                    option
                  ) =>
                    setFieldValue(
                      "phoneNumberCountryCode",
                      option?.value ||
                        ""
                    )
                  }
                  formatOptionLabel={(
                    option
                  ) => (
                    <div className="edit-industry-option-with-icon">
                      <img
                        src={`https://flagcdn.com/w40/${option.code}.png`}
                        alt={
                          option.label
                        }
                        className="edit-industry-option-flag"
                      />

                      <span>
                        {
                          option.label
                        }
                      </span>
                    </div>
                  )}
                  placeholder={t(
                    "placeholders.phoneNumberCountryCode"
                  )}
                  styles={
                    customSelectStyles
                  }
                  classNamePrefix="edit-industry-select"
                  isClearable
                />
              </div>

              <div className="edit-industry-field">
                <label htmlFor="phoneNumber">
                  {t(
                    "labels.phoneNumber"
                  )}
                </label>

                <input
                  id="phoneNumber"
                  name="phoneNumber"
                  value={
                    industry.phoneNumber
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.phoneNumber"
                  )}
                  className="edit-industry-input"
                />
              </div>
            </div>

            <div className="edit-industry-grid edit-industry-grid--1">
              <div className="edit-industry-field">
                <label htmlFor="invoiceEmail">
                  {t(
                    "labels.invoiceEmail"
                  )}
                </label>

                <input
                  type="email"
                  id="invoiceEmail"
                  name="invoiceEmail"
                  value={
                    industry.invoiceEmail
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.invoiceEmail"
                  )}
                  className="edit-industry-input"
                />
              </div>
            </div>

            <div className="edit-industry-subsection-header">
              <div>
                <h3>
                  {t(
                    "contacts.section_title"
                  )}
                </h3>

                <p>
                  {t(
                    "contacts.section_subtitle"
                  )}
                </p>
              </div>

              <button
                type="button"
                className="edit-industry-add-user-button"
                onClick={
                  handleAddContact
                }
              >
                <FaPlus />

                <span>
                  {t(
                    "contacts.actions.add"
                  )}
                </span>
              </button>
            </div>

            {(industry.contacts ||
              []).length ===
            0 ? (
              <div className="edit-industry-empty-contacts">
                {t(
                  "contacts.empty"
                )}
              </div>
            ) : (
              <div className="edit-industry-extra-contacts">
                {(
                  industry.contacts ||
                  []
                ).map(
                  (
                    contact,
                    index
                  ) => (
                    <div
                      key={
                        index
                      }
                      className="edit-industry-extra-contact-block"
                    >
                      <div className="edit-industry-extra-contact-heading">
                        <strong>
                          {t(
                            "contacts.additional_title",
                            {
                              number:
                                index +
                                1,
                            }
                          )}
                        </strong>

                        <button
                          type="button"
                          className="edit-industry-remove-user-button"
                          onClick={() =>
                            handleRemoveContact(
                              index
                            )
                          }
                          title={t(
                            "contacts.actions.remove"
                          )}
                          aria-label={t(
                            "contacts.actions.remove"
                          )}
                        >
                          <FaTrashAlt />
                        </button>
                      </div>

                      <div className="edit-industry-extra-contact-row">
                        <div className="edit-industry-field">
                          <label>
                            {t(
                              "contacts.fields.name"
                            )}
                          </label>

                          <input
                            value={
                              contact.contactName ||
                              ""
                            }
                            onChange={(
                              event
                            ) =>
                              handleContactChange(
                                index,
                                "contactName",
                                event
                                  .target
                                  .value
                              )
                            }
                            placeholder={t(
                              "contacts.placeholders.name"
                            )}
                            className="edit-industry-input"
                          />
                        </div>

                        <div className="edit-industry-field">
                          <label>
                            {t(
                              "contacts.fields.title"
                            )}
                          </label>

                          <input
                            value={
                              contact.contactTitle ||
                              ""
                            }
                            onChange={(
                              event
                            ) =>
                              handleContactChange(
                                index,
                                "contactTitle",
                                event
                                  .target
                                  .value
                              )
                            }
                            placeholder={t(
                              "contacts.placeholders.title"
                            )}
                            className="edit-industry-input"
                          />
                        </div>

                        <div className="edit-industry-field">
                          <label>
                            {t(
                              "contacts.fields.email"
                            )}
                          </label>

                          <input
                            type="email"
                            value={
                              contact.contactEmail ||
                              ""
                            }
                            onChange={(
                              event
                            ) =>
                              handleContactChange(
                                index,
                                "contactEmail",
                                event
                                  .target
                                  .value
                              )
                            }
                            placeholder={t(
                              "contacts.placeholders.email"
                            )}
                            className={`edit-industry-input ${
                              invalidFields[
                                `contacts-${index}-contactEmail`
                              ]
                                ? "edit-industry-input-error"
                                : ""
                            }`}
                          />

                          {invalidFields[
                            `contacts-${index}-contactEmail`
                          ] && (
                            <p className="edit-industry-field-error">
                              {
                                invalidFields[
                                  `contacts-${index}-contactEmail`
                                ]
                              }
                            </p>
                          )}
                        </div>

                        <div className="edit-industry-field">
                          <label>
                            {t(
                              "contacts.fields.countryCode"
                            )}
                          </label>

                          <Select
                            options={
                              phoneNumberOptions
                            }
                            value={
                              phoneNumberOptions.find(
                                (
                                  option
                                ) =>
                                  option.value ===
                                  contact.phoneNumberCountryCode
                              ) ||
                              null
                            }
                            onChange={(
                              option
                            ) =>
                              handleContactChange(
                                index,
                                "phoneNumberCountryCode",
                                option?.value ||
                                  ""
                              )
                            }
                            formatOptionLabel={(
                              option
                            ) => (
                              <div className="edit-industry-option-with-icon">
                                <img
                                  src={`https://flagcdn.com/w40/${option.code}.png`}
                                  alt={
                                    option.label
                                  }
                                  className="edit-industry-option-flag"
                                />

                                <span>
                                  {
                                    option.label
                                  }
                                </span>
                              </div>
                            )}
                            placeholder={t(
                              "contacts.placeholders.countryCode"
                            )}
                            styles={
                              customSelectStyles
                            }
                            classNamePrefix="edit-industry-select"
                            isClearable
                          />
                        </div>

                        <div className="edit-industry-field">
                          <label>
                            {t(
                              "contacts.fields.phoneNumber"
                            )}
                          </label>

                          <input
                            value={
                              contact.phoneNumber ||
                              ""
                            }
                            onChange={(
                              event
                            ) =>
                              handleContactChange(
                                index,
                                "phoneNumber",
                                event
                                  .target
                                  .value
                              )
                            }
                            placeholder={t(
                              "contacts.placeholders.phoneNumber"
                            )}
                            className="edit-industry-input"
                          />
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </section>

          {/* BILLING */}
          <section
            ref={(
              node
            ) => {
              sectionRefs.current.billing =
                node;
            }}
            data-section="billing"
            className="edit-industry-section"
          >
            <div className="edit-industry-section-header">
              <span className="edit-industry-section-header__icon">
                <FaCreditCard />
              </span>

              <div>
                <h2>
                  {t(
                    "nav.billing"
                  )}
                </h2>

                <p>
                  {t(
                    "section_subtitles.billing"
                  )}
                </p>
              </div>
            </div>

            <div className="edit-industry-grid edit-industry-grid--2">
              <div className="edit-industry-field">
                <label htmlFor="accountHolderName">
                  {t(
                    "labels.accountHolderName"
                  )}
                </label>

                <input
                  id="accountHolderName"
                  name="accountHolderName"
                  value={
                    industry.accountHolderName
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.accountHolderName"
                  )}
                  className="edit-industry-input"
                />
              </div>

              <div className="edit-industry-field">
                <label htmlFor="accountNumber">
                  {t(
                    "labels.accountNumber"
                  )}
                </label>

                <input
                  id="accountNumber"
                  name="accountNumber"
                  value={
                    industry.accountNumber
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.accountNumber"
                  )}
                  className="edit-industry-input"
                />
              </div>
            </div>

            <div className="edit-industry-grid edit-industry-grid--4">
              <div className="edit-industry-field">
                <label htmlFor="bankName">
                  {t(
                    "labels.bankName"
                  )}
                </label>

                <input
                  id="bankName"
                  name="bankName"
                  value={
                    industry.bankName
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.bankName"
                  )}
                  className="edit-industry-input"
                />
              </div>

              <div className="edit-industry-field">
                <label htmlFor="IBAN">
                  {t(
                    "labels.IBAN"
                  )}
                </label>

                <input
                  id="IBAN"
                  name="IBAN"
                  value={
                    industry.IBAN
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.IBAN"
                  )}
                  className={`edit-industry-input ${
                    invalidFields.IBAN
                      ? "edit-industry-input-error"
                      : ""
                  }`}
                />

                {invalidFields.IBAN && (
                  <p className="edit-industry-field-error">
                    {
                      invalidFields.IBAN
                    }
                  </p>
                )}
              </div>

              <div className="edit-industry-field">
                <label htmlFor="SWIFT_BIC">
                  {t(
                    "labels.SWIFT_BIC"
                  )}
                </label>

                <input
                  id="SWIFT_BIC"
                  name="SWIFT_BIC"
                  value={
                    industry.SWIFT_BIC
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.SWIFT_BIC"
                  )}
                  className={`edit-industry-input ${
                    invalidFields.SWIFT_BIC
                      ? "edit-industry-input-error"
                      : ""
                  }`}
                />

                {invalidFields.SWIFT_BIC && (
                  <p className="edit-industry-field-error">
                    {
                      invalidFields.SWIFT_BIC
                    }
                  </p>
                )}
              </div>

              <div className="edit-industry-field">
                <label>
                  {t(
                    "labels.currency"
                  )}
                </label>

                <Select
                  options={
                    currencyOptions
                  }
                  value={
                    currencyOptions.find(
                      (
                        option
                      ) =>
                        option.value ===
                        industry.currency
                    ) ||
                    null
                  }
                  onChange={(
                    option
                  ) =>
                    setFieldValue(
                      "currency",
                      option?.value ||
                        ""
                    )
                  }
                  formatOptionLabel={(
                    option
                  ) => (
                    <div className="edit-industry-option-with-icon">
                      <img
                        src={`https://flagcdn.com/w40/${option.code}.png`}
                        alt={
                          option.label
                        }
                        className="edit-industry-option-flag"
                      />

                      <span>
                        {
                          option.label
                        }
                      </span>
                    </div>
                  )}
                  placeholder={t(
                    "placeholders.currency"
                  )}
                  styles={
                    customSelectStyles
                  }
                  classNamePrefix="edit-industry-select"
                  isClearable
                  isSearchable={
                    false
                  }
                />
              </div>
            </div>

            <div className="edit-industry-grid edit-industry-grid--4">
              <div className="edit-industry-field">
                <label>
                  {t(
                    "labels.paymentMethodsAccepted"
                  )}
                </label>

                <Select
                  options={
                    paymentOptions
                  }
                  isMulti
                  closeMenuOnSelect={
                    false
                  }
                  hideSelectedOptions={
                    false
                  }
                  value={
                    paymentOptions.filter(
                      (
                        option
                      ) =>
                        (
                          industry.paymentMethodsAccepted ||
                          []
                        ).includes(
                          option.value
                        )
                    )
                  }
                  onChange={(
                    selected
                  ) =>
                    setFieldValue(
                      "paymentMethodsAccepted",
                      selected
                        ? selected.map(
                            (
                              option
                            ) =>
                              option.value
                          )
                        : []
                    )
                  }
                  placeholder={t(
                    "placeholders.paymentMethodsAccepted"
                  )}
                  styles={
                    paymentMultiStyles
                  }
                  classNamePrefix="edit-industry-select"
                  isSearchable={
                    false
                  }
                />
              </div>

              <div className="edit-industry-field">
                <label>
                  {t(
                    "labels.paymentDueDate"
                  )}
                </label>

                <Select
                  options={
                    paymentDueDateOptions
                  }
                  value={
                    paymentDueDateOptions.find(
                      (
                        option
                      ) =>
                        option.value ===
                        industry.paymentDueDate
                    ) ||
                    null
                  }
                  onChange={(
                    option
                  ) =>
                    setFieldValue(
                      "paymentDueDate",
                      option?.value ||
                        ""
                    )
                  }
                  placeholder={t(
                    "placeholders.paymentDueDate"
                  )}
                  styles={
                    customSelectStyles
                  }
                  classNamePrefix="edit-industry-select"
                  isClearable
                  isSearchable={
                    false
                  }
                />
              </div>

              <div className="edit-industry-field">
                <label>
                  {t(
                    "labels.discountTerms"
                  )}
                </label>

                <Select
                  options={[
                    ...Array(
                      21
                    ),
                  ].map(
                    (
                      _,
                      index
                    ) => ({
                      value:
                        `${index *
                        5}%`,

                      label:
                        `${index *
                        5}%`,
                    })
                  )}
                  value={
                    industry.discountTerms
                      ? {
                          value:
                            industry.discountTerms,

                          label:
                            industry.discountTerms,
                        }
                      : null
                  }
                  onChange={(
                    option
                  ) =>
                    setFieldValue(
                      "discountTerms",
                      option?.value ||
                        ""
                    )
                  }
                  placeholder={t(
                    "placeholders.discountTerms"
                  )}
                  styles={
                    customSelectStyles
                  }
                  classNamePrefix="edit-industry-select"
                  isClearable
                  isSearchable={
                    false
                  }
                />
              </div>

              <div className="edit-industry-field">
                <label htmlFor="latePaymentPenalties">
                  {t(
                    "labels.latePaymentPenalties"
                  )}
                </label>

                <input
                  id="latePaymentPenalties"
                  name="latePaymentPenalties"
                  value={
                    industry.latePaymentPenalties
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.latePaymentPenalties"
                  )}
                  className="edit-industry-input"
                />
              </div>
            </div>

            <div className="edit-industry-section-divider" />

            <div className="edit-industry-subsection-heading">
              <h3>
                {t(
                  "sections.invoicing_prefs"
                )}
              </h3>

              <p>
                {t(
                  "section_subtitles.invoicing"
                )}
              </p>
            </div>

            <div className="edit-industry-grid edit-industry-grid--3">
              <div className="edit-industry-field">
                <label htmlFor="VAT_TaxInformation">
                  {t(
                    "labels.VAT_TaxInformation"
                  )}
                </label>

                <input
                  id="VAT_TaxInformation"
                  name="VAT_TaxInformation"
                  value={
                    industry.VAT_TaxInformation
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.VAT_TaxInformation"
                  )}
                  className="edit-industry-input"
                />
              </div>

              <div className="edit-industry-field">
                <label htmlFor="iva">
                  {t(
                    "labels.iva"
                  )}
                </label>

                <input
                  id="iva"
                  name="iva"
                  inputMode="decimal"
                  value={
                    industry.iva
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.iva"
                  )}
                  className={`edit-industry-input ${
                    invalidFields.iva
                      ? "edit-industry-input-error"
                      : ""
                  }`}
                />

                {invalidFields.iva && (
                  <p className="edit-industry-field-error">
                    {
                      invalidFields.iva
                    }
                  </p>
                )}
              </div>

              <div className="edit-industry-field">
                <label>
                  {t(
                    "labels.invoiceDeliveryMethod"
                  )}
                </label>

                <Select
                  options={
                    invoiceDeliveryOptions
                  }
                  value={
                    invoiceDeliveryOptions.find(
                      (
                        option
                      ) =>
                        option.value ===
                        industry.invoiceDeliveryMethod
                    ) ||
                    null
                  }
                  onChange={(
                    option
                  ) =>
                    setFieldValue(
                      "invoiceDeliveryMethod",
                      option?.value ||
                        ""
                    )
                  }
                  placeholder={t(
                    "placeholders.invoiceDeliveryMethod"
                  )}
                  styles={
                    customSelectStyles
                  }
                  classNamePrefix="edit-industry-select"
                  isClearable
                  isSearchable={
                    false
                  }
                />
              </div>
            </div>

            <div className="edit-industry-grid edit-industry-grid--1">
              <div className="edit-industry-field">
                <label htmlFor="additionalNotes">
                  {t(
                    "labels.additionalNotes"
                  )}
                </label>

                <textarea
                  id="additionalNotes"
                  name="additionalNotes"
                  value={
                    industry.additionalNotes ||
                    ""
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.additionalNotes"
                  )}
                  rows="4"
                  maxLength="500"
                  className="edit-industry-textarea"
                />

                <div className="edit-industry-character-counter">
                  {(
                    industry.additionalNotes ||
                    ""
                  ).length}{" "}
                  / 500
                </div>
              </div>
            </div>
          </section>
        </form>
      </div>
    </div>
  );
}